/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useSavedViews } from '@/components/ui/saved-views/useSavedViews'
import { useDashboard } from '@/hooks'

import { type ColdStorageSavedView, useColdStorage } from './ColdStorageContext'
import { ColdStorageList } from './ColdStorageList'
import { ColdStorageToolbar } from './ColdStorageToolbar'
import { FloorPlan } from './FloorPlan'
import { FloorPlansModal } from './FloorPlansModal'
import { LocationDetail } from './LocationDetail'
import { LocationRangeModal } from './LocationRangeModal'
import { SensorDetail } from './SensorDetail'

/** The list's header row height before it has been measured. */
const DEFAULT_HEADER_HEIGHT = 36

/**
 * The page body: toolbar on top, the grouped location/sensor list on the left and the
 * selected location's floor plan on the right, split by a drag handle (the tracker map's).
 * A location's or sensor's details open in a sheet over the floor plan, its title bar level with
 * the list's header row.
 */
export const ColdStorage = () => {
  const context = useColdStorage()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isResizing, setIsResizing] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT)

  // The list's header row sits at the top of its column, so the sheet only needs its height. Read
  // after every render (the table appears once the state loads), set only when it changes.
  useLayoutEffect(() => {
    const height = listRef.current?.querySelector('thead')?.getBoundingClientRect().height
    if (height && height !== headerHeight) setHeaderHeight(height)
  })
  const tableHeader = { top: 0, left: 0, height: headerHeight }

  // Once the profile is in, open the default saved view, unless this tab already has its own
  // state (coming back to the page keeps what the user left).
  const { isLoading: isDashboardLoading } = useDashboard()
  const { defaultView } = useSavedViews<ColdStorageSavedView>(context.mode.mode)
  const defaultChecked = useRef(false)
  useEffect(() => {
    if (isDashboardLoading || defaultChecked.current) return
    defaultChecked.current = true
    if (defaultView && !context.companyHydrated) context.applyView(defaultView)
  }, [isDashboardLoading, defaultView, context])

  useEffect(() => {
    if (!isResizing) return
    const onMove = (e: MouseEvent) => {
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      context.setListWidth(Math.max(context.mode.minListWidth, Math.min(rect.width - 320, e.clientX - rect.left)))
    }
    const onUp = () => setIsResizing(false)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isResizing, context])

  return (
    <div className="flex flex-col min-w-0 overflow-x-hidden h-full w-full relative flex-1">
      <ColdStorageToolbar />
      <div ref={containerRef} className="flex flex-1 min-h-0 min-w-0">
        <div
          ref={listRef}
          className="min-w-0 min-h-0 flex flex-col bg-base-100 h-full shrink-0"
          style={{ width: `${context.listWidth}px` }}
        >
          <ColdStorageList />
        </div>
        <div
          onMouseDown={(e) => {
            e.preventDefault()
            setIsResizing(true)
          }}
          onDoubleClick={() => context.setListWidth(context.mode.defaultListWidth)}
          className={`w-1.5 h-full cursor-col-resize transition-colors shrink-0 ${
            isResizing ? 'bg-primary' : 'bg-base-300 hover:bg-primary/60'
          }`}
          title="Drag to resize, double-click to reset"
        />
        <div className="relative flex-1 min-w-0 min-h-0 h-full">
          <FloorPlan />
          <SensorDetail tableHeader={tableHeader} />
          <LocationDetail tableHeader={tableHeader} />
        </div>
      </div>

      <LocationRangeModal />
      <FloorPlansModal />
    </div>
  )
}
